/** Formatação para exibição. Locale fixo em pt-BR: o produto é brasileiro. */

const NUMERO = new Intl.NumberFormat('pt-BR');
const DATA_HORA = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function numero(n: number): string {
  return NUMERO.format(n);
}

export function dataHora(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : DATA_HORA.format(d);
}

/** "há 4 min", "ontem", "há 3 dias" — como nas telas da IDV. */
export function tempoRelativo(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '—';

  const seg = Math.floor((Date.now() - d) / 1000);
  if (seg < 60) return 'agora';
  const min = Math.floor(seg / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const dias = Math.floor(h / 24);
  if (dias === 1) return 'ontem';
  if (dias < 30) return `há ${dias} dias`;
  return dataHora(iso);
}

/** Agrupa a chave de 44 dígitos de 4 em 4 para conferência visual. */
export function chaveLegivel(chave: string): string {
  return chave.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

export function porcentagem(v: number): string {
  return `${(v * 100).toFixed(v >= 0.999 ? 0 : 1).replace('.', ',')}%`;
}

/**
 * Extrai as chaves de 44 dígitos de um texto colado, deduplicando, e só as
 * com dígito verificador válido — a mesma regra do servidor
 * (`src/common/chave.util.ts`), para a contagem e o custo baterem.
 */
export function extrairChaves(texto: string): string[] {
  return Array.from(new Set(texto.match(/\d{44}/g) ?? [])).filter(chaveValida);
}

/** Quantas sequências de 44 dígitos têm o dígito verificador errado. */
export function chavesComDigitoErrado(texto: string): number {
  return (
    new Set(texto.match(/\d{44}/g) ?? []).size - extrairChaves(texto).length
  );
}

function chaveValida(chave: string): boolean {
  let soma = 0;
  let peso = 2;
  for (let i = 42; i >= 0; i--) {
    soma += Number(chave[i]) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }
  const resto = soma % 11;
  return (resto < 2 ? 0 : 11 - resto) === Number(chave[43]);
}

export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

/** `1234` → `R$ 12,34`. Centavos inteiros, como no backend. */
export function reais(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

/**
 * Vazão sustentada de download, em XML por segundo.
 *
 * Medida em produção: a fonte (`consultadanfe`) throttla por IP e cada consulta
 * dela leva de 5 a 40 s. Não é limite nosso, e jogar mais concorrência não
 * ajuda — por isso a estimativa é uma FAIXA. Prometer um número único seria
 * prometer o que não depende de nós.
 *
 * Os extremos são os que fecham com as projeções aferidas (783 mil XMLs em 10 a
 * 18 dias): 783000/0,9 ≈ 10 dias, 783000/0,5 ≈ 18 dias. O 0,3 XML/s que aparece
 * no README é o piso de um IP único sem proxy rotativo — cenário que a operação
 * não usa, e adotá-lo aqui inflaria a estimativa em quase o dobro.
 */
const VAZAO_MIN_XML_S = 0.5;
const VAZAO_MAX_XML_S = 0.9;

/** "12 min", "3 h", "2 dias" — arredondado para a unidade que se lê. */
function duracao(segundos: number): string {
  if (segundos < 90) return 'menos de 2 min';
  const min = Math.round(segundos / 60);
  if (min < 60) return `${min} min`;
  const horas = segundos / 3600;
  if (horas < 48) return `${Math.round(horas)} h`;
  return `${Math.round(horas / 24)} dias`;
}

/**
 * Quanto tempo deve levar para baixar `chaves` XMLs.
 *
 * Devolve algo como "de 16 h a 2 dias". Quando as duas pontas arredondam para
 * o mesmo texto, devolve só um valor — "de 5 min a 5 min" não informa nada.
 */
export function estimativaDownload(chaves: number): string {
  if (chaves <= 0) return '—';
  const rapido = duracao(chaves / VAZAO_MAX_XML_S);
  const lento = duracao(chaves / VAZAO_MIN_XML_S);
  return rapido === lento ? rapido : `de ${rapido} a ${lento}`;
}

/**
 * `plural(1, 'chave')` → "1 chave"; `plural(3, 'chave')` → "3 chaves".
 * Plural irregular no terceiro argumento: `plural(n, 'nota fiscal', 'notas fiscais')`.
 */
export function plural(
  n: number,
  singular: string,
  pluralForma = `${singular}s`,
): string {
  return `${numero(n)} ${n === 1 ? singular : pluralForma}`;
}
